import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import SEO from '../components/SEO';

function OurServices() {
  const { t } = useTranslation();
  const [ref, inView] = useInView({ triggerOnce: true });
  const controls = useAnimation();

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const fadeIn = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }, // 調整動畫效果
  };

  const serviceData = [ // 將服務項目資料提取出來，方便管理和擴展
    {
      titleKey: 'services.blockchainFraud.title',
      descriptionKey: 'services.blockchainFraud.description',
      icon: <i className="fas fa-shield-alt fa-3x text-blue-500 mb-4"></i>, // 加入圖示
    },
    {
      titleKey: 'services.web3Education.title',
      descriptionKey: 'services.web3Education.description',
      icon: <i className="fas fa-graduation-cap fa-3x text-green-500 mb-4"></i>,
    },
    {
      titleKey: 'services.blockchainConsulting.title',
      descriptionKey: 'services.blockchainConsulting.description',
      icon: <i className="fas fa-chart-line fa-3x text-yellow-500 mb-4"></i>,
    },
    {
      titleKey: 'services.riskManagement.title',
      descriptionKey: 'services.riskManagement.description',
      icon: <i className="fas fa-exclamation-triangle fa-3x text-red-500 mb-4"></i>,
    },
  ];

  const processData = [ // 合作流程資料
    { titleKey: 'services.process.step1', descriptionKey: 'services.process.description1', icon: <i className="fas fa-lightbulb fa-2x text-blue-500 mb-2"></i> },
    { titleKey: 'services.process.step2', descriptionKey: 'services.process.description2', icon: <i className="fas fa-handshake fa-2x text-green-500 mb-2"></i> },
    { titleKey: 'services.process.step3', descriptionKey: 'services.process.description3', icon: <i className="fas fa-cogs fa-2x text-yellow-500 mb-2"></i> },
    { titleKey: 'services.process.step4', descriptionKey: 'services.process.description4', icon: <i className="fas fa-check-circle fa-2x text-purple-500 mb-2"></i> },
  ];

  return (
    <>
      <SEO 
        path="/services"
        title={t('services.title')}
        description={t('services.subtitle')}
        keywords="DamenKYT, blockchain security, risk assessment, wallet analysis, blockchain consulting, Web3 education"
        ogTitle={t('services.title')}
        ogDescription={t('services.subtitle')}
        ogImage="/images/myReportsBanner.png"
        twitterCard="summary_large_image"
        twitterTitle={t('services.title')}
        twitterDescription={t('services.subtitle')}
        twitterImage="/images/myReportsBanner.png"
        canonicalUrl={window.location.href}
        robots="index, follow"
        siteName="DamenKYT"
        image="/images/myReportsBanner.png"
        imageWidth="1200"
        imageHeight="630"
        imageAlt={t('services.title')}
      />
      <div className="bg-gradient-to-b from-gray-100 to-white dark:bg-gradient-to-b dark:from-gray-900 dark:to-gray-800 min-h-screen text-black dark:text-white font-sans"> {/* 使用漸層背景和更現代的字體 */}
        <div
          className="w-full h-64 bg-cover bg-center flex items-center justify-center text-white text-3xl font-bold"
          style={{ backgroundImage: `url('/images/myReportsBanner.png')` }}
        >
          {t('services.title')}
        </div>

        <header className="text-center py-20"> {/* 加大標題區塊的padding */}
          <p className="text-xl mt-6 text-gray-600 dark:text-gray-400">{t('services.subtitle')}</p> {/* 調整副標題樣式 */}
        </header>

        <div className="container mx-auto px-6 md:px-12 lg:px-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-20"> {/* 調整間距和使用更大的container */}
          {serviceData.map((service, index) => (
            <motion.div
              ref={index === 0 ? ref : null} // 只有第一個元素使用ref
              key={index}
              className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-lg hover:scale-105 transition-transform duration-300 ease-in-out border border-gray-200 dark:border-gray-700" // 更精緻的卡片樣式
              variants={fadeIn}
              initial="hidden"
              animate={controls}
            >
              {service.icon} {/* 渲染圖示 */}
              <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-100">{t(service.titleKey)}</h2>
              <p className="text-gray-600 dark:text-gray-400">{t(service.descriptionKey)}</p>
            </motion.div>
          ))}
        </div>

        <section className="py-24 bg-gray-50 dark:bg-gray-850"> {/* 調整背景顏色 */}
          <div className="container mx-auto px-6 md:px-12 lg:px-24">
            <h2 className="text-3xl font-bold text-center mb-12 tracking-tight leading-tight">{t('services.process.title')}</h2> {/* 調整標題字體 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
              {processData.map((process, index) => (
                <motion.div
                  key={index}
                  className="text-center"
                  variants={fadeIn}
                  initial="hidden"
                  animate={controls}
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-500 mb-6"> {/* 圓形背景圖示 */}
                    {process.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-100">{t(process.titleKey)}</h3>
                  <p className="text-gray-600 dark:text-gray-400">{t(process.descriptionKey)}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default OurServices;